import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleLoginButton } from './GoogleLoginButton';
import { FormGroup, FormLabel, FormInput, FormButton } from './FormComponents';
import './AuthForm.css';

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister, onSwitchToForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, error, clearError } = useAuth();

  // Check if Google OAuth is configured
  const isGoogleConfigured = process.env.REACT_APP_GOOGLE_CLIENT_ID && 
    process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'placeholder-get-from-google-console';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    clearError();

    try {
      await login(email, password);
      // Success - user will be logged in via context
    } catch (error) {
      // Error is handled by the context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) clearError();
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (error) clearError();
  };

  return (
    <div className="auth-form-container">
      <h2 className="auth-form-title">Login</h2>
      
      {error && (
        <div className="auth-error-message">
          {error}
        </div>
      )}

      {/* Google Login Button - only show if configured */}
      {isGoogleConfigured && (
        <>
          <GoogleLoginButton
            text="signin_with"
            onSuccess={() => {
              // Success handled by AuthContext
            }}
            onError={(error) => {
              console.error('Google login error:', error);
            }}
          />

          <div className="auth-divider">
            <span className="auth-divider-text">OR</span>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit} className="auth-form">
        <FormGroup>
          <FormLabel htmlFor="email">
            Email
          </FormLabel>
          <FormInput
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            required
            disabled={isSubmitting}
            placeholder="Enter your email"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel htmlFor="password">
            Password
          </FormLabel>
          <FormInput
            type="password"
            id="password"
            value={password}
            onChange={handlePasswordChange}
            required
            disabled={isSubmitting}
            placeholder="Enter your password"
            minLength={6}
          />
        </FormGroup>

        <FormButton
          type="submit"
          disabled={isSubmitting || !email || !password}
          variant="primary"
        >
          {isSubmitting ? 'Logging in...' : 'Login'}
        </FormButton>
      </form>

      <div className="auth-switch-container">
        {onSwitchToForgotPassword && (
          <p>
            <button
              type="button"
              onClick={onSwitchToForgotPassword}
              className="auth-switch-button"
              disabled={isSubmitting}
            >
              Forgot Password?
            </button>
          </p>
        )}
        <p>
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="auth-switch-button"
            disabled={isSubmitting}
          >
            Register Here
          </button>
        </p>
      </div>
    </div>
  );
};